import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { getMe } from '@cocoonspace/sdk-js/domains/profile/profile'
import { authEmitter } from '@cocoonspace/shared/domains/auth/emitters/auth.emitter'
import { useValidateEmail } from '@cocoonspace/shared/hooks/use-emailable'
import { Button, buttonVariants } from '@cocoonspace/ui/components/button'
import { Form } from '@cocoonspace/ui/components/form'
import { FormControlledInput } from '@cocoonspace/ui/components/form-controlled-input'
import { PasswordCreationInput } from '@cocoonspace/ui/components/password-creation-input'
import { Separator } from '@cocoonspace/ui/components/separator'
import { zodResolver } from '@hookform/resolvers/zod'
import { Trans, useLingui } from '@lingui/react/macro'
import { createUserWithEmailAndPassword } from 'firebase/auth'
import { LocalizedLink as Link } from '~/components/localized-link'
import { useAnalytics } from 'use-analytics'
import * as z from 'zod'

import { ValidatedEmailInput } from '~/components/ui/validated-email-input'
import { useHttpErrorMsg } from '~/lib/get-http-error-msg'
import { getAuthInstance, useAuth } from '~/providers/auth-provider'
import {
	AppleConnectButton,
	GoogleConnectButton,
	MicrosoftConnectButton,
} from './social-auth-list'

type FormData = z.infer<typeof registerSchema>

const registerSchema = z.object({
	email: z.email(),
	password: z
		.string()
		.min(8, {
			message: `Votre mot de passe doit comporter au moins 8 caractères`,
		})
		.regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/, {
			message: `Votre mot de passe doit contenir au moins une lettre majuscule, une lettre minuscule et un chiffre`,
		}),
})

const Connect = () => {
	const { t } = useLingui()
	const { setAuthDialogActiveTab } = useAuth()

	return (
		<div className='flex flex-row justify-center gap-x-2 px-6'>
			{t`Vous avez déjà un compte ?`}

			<button
				className='text-primary'
				onClick={() => setAuthDialogActiveTab('login')}
				type='button'
			>
				{t`Se connecter`}
			</button>
		</div>
	)
}

export const RegisterForm = ({
	onSuccess,
	hideLogin,
}: {
	onSuccess?: () => void
	hideLogin?: boolean
}) => {
	const httpErrorMsg = useHttpErrorMsg()
	const { t } = useLingui()
	const { track } = useAnalytics()
	const { setIsAuthDialogOpen, authDialogInitialData } = useAuth()

	const [authError, setAuthError] = useState<string | null>(null)

	const form = useForm<FormData>({
		resolver: zodResolver(registerSchema),
		values: {
			email: '',
			password: '',
			...authDialogInitialData,
		},
	})

	const { validate } = useValidateEmail(form)

	const onSignUpSuccess = () => {
		return getMe().then((res) => {
			track('register', { analytics_id: res.data?.analytics_id })
			onSuccess?.()
		})
	}

	const onSubmitForm = async (data: FormData) => {
		const isValid = await validate(data.email)

		if (isValid) {
			const auth = getAuthInstance()

			return createUserWithEmailAndPassword(auth, data.email, data.password)
				.then((res) => {
					if (res?.user) {
						authEmitter.emit('register.success')
						setIsAuthDialogOpen(false)
						onSignUpSuccess()
					}
				})
				.catch((error) => {
					setAuthError(error.code)
				})
		}
	}

	return (
		<div className='flex flex-col gap-3'>
			{!!authError && (
				<p className='text-primary mb-4 px-6 text-sm'>
					{httpErrorMsg[authError]?.() || t`Une erreur est survenue`}
				</p>
			)}

			<form
				className='flex flex-col gap-y-6 px-6'
				noValidate
				onSubmit={form.handleSubmit(onSubmitForm)}
			>
				<Form {...form}>
					<div className='flex flex-col'>
						<ValidatedEmailInput
							label={t`Email`}
							name='email'
							required
						/>

						<FormControlledInput
							control={form.control}
							label={t`Mot de passe`}
							name='password'
							render={({ field }) => (
								<PasswordCreationInput
									{...field}
									required
								/>
							)}
							required
						/>

						<p className='text-center text-sm'>
							<Trans>
								En vous inscrivant, vous acceptez les <br />
								<Link
									className={buttonVariants({ size: 'sm', variant: 'link' })}
									href='/mentions-legales'
									target='_blank'
								>
									conditions générales d'utilisation
								</Link>
								.
							</Trans>
						</p>
					</div>

					<div className='flex flex-col gap-y-4'>
						<Button
							className='w-full'
							data-testid='register-btn'
							disabled={form.formState.isSubmitting}
							type='submit'
						>
							{t`Créer mon compte`}
						</Button>

						<div className='flex flex-row items-center gap-x-4 leading-none'>
							<Separator className='flex-1' />
							<div className='text-center'>{t`Ou`}</div>
							<Separator className='flex-1' />
						</div>

						<GoogleConnectButton
							action='register'
							onSuccess={onSignUpSuccess}
						/>

						<AppleConnectButton
							action='register'
							onSuccess={onSignUpSuccess}
						/>

						<MicrosoftConnectButton
							action='register'
							onSuccess={onSignUpSuccess}
						/>
					</div>
				</Form>
			</form>

			{!hideLogin && (
				<>
					<Separator />
					<Connect />
				</>
			)}
		</div>
	)
}
